import { useEffect, useState, useCallback } from "react";
import { UserCheck, Plus, X, Download, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { DataTable } from "@/components/common/DataTable";
import {
  PageHeader, TenantPicker, useTenantScope, Badge, fmtDate, DSAR_TYPES, DSAR_STATUSES,
} from "./shared";

export default function PrivacyRequestsPage() {
  const scope = useTenantScope();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ status: "", type: "" });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "access", requesterEmail: "", requesterName: "", customerId: "", message: "" });
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = { ...scope.params, size: 100 };
    if (filters.status) params.status = filters.status;
    if (filters.type) params.type = filters.type;
    try { setData((await api.get("/v1/compliance/privacy-requests", { params })).data.data); }
    catch { setData({ items: [], total: 0 }); }
    finally { setLoading(false); }
  }, [scope.params, filters]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.requesterEmail.trim()) { toast.error("Requester email is required"); return; }
    try {
      await api.post("/v1/compliance/privacy-requests", {
        ...form, customerId: form.customerId || null, ...scope.params,
      });
      toast.success("Privacy request created");
      setShowForm(false);
      setForm({ type: "access", requesterEmail: "", requesterName: "", customerId: "", message: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const openDetail = async (row) => {
    try { setActive((await api.get(`/v1/compliance/privacy-requests/${row.id}`)).data.data); }
    catch { setActive(row); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/v1/compliance/privacy-requests/${active.id}/status`, { status });
      toast.success(`Status → ${status}`);
      setActive({ ...active, status });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const fulfill = async () => {
    try {
      const res = await api.post(`/v1/compliance/privacy-requests/${active.id}/fulfill`);
      toast.success(active.type === "erasure" ? "Subject anonymized" : "Data package compiled");
      await openDetail(active);
      load();
      return res.data.data;
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/v1/compliance/privacy-requests/${active.id}/notes`, { body: note });
      setNote("");
      await openDetail(active);
      toast.success("Note added");
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const downloadPackage = async () => {
    try {
      const res = await api.get(`/v1/compliance/privacy-requests/${active.id}/export`);
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${active.requestNumber}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canFulfill = active && ["access", "export", "portability", "erasure"].includes(active.type)
    && active.status !== "Completed";

  return (
    <div data-testid="privacy-requests-page">
      <PageHeader icon={UserCheck} title="Privacy Requests (DSAR)"
        subtitle={`Data-subject requests · ${data.total} total`}>
        <TenantPicker {...scope} />
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5 transition-colors" data-testid="new-dsar-btn">
          <Plus className="h-4 w-4" /> New request
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-4">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="filter-status">
          <option value="">All statuses</option>
          {DSAR_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="filter-type">
          <option value="">All types</option>
          {DSAR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <DataTable testid="dsar-table" rows={data.items} loading={loading}
        columns={[
          { key: "requestNumber", header: "Request", render: (r) => <button onClick={() => openDetail(r)} className="font-mono text-xs text-teal-600 hover:underline" data-testid={`dsar-open-${r.requestNumber}`}>{r.requestNumber}</button> },
          { key: "type", header: "Type", render: (r) => <span className="capitalize">{r.type}</span> },
          { key: "requesterEmail", header: "Requester", render: (r) => r.requesterEmail },
          { key: "status", header: "Status", render: (r) => <Badge label={r.status} kind="status" /> },
          { key: "dueDate", header: "Due", render: (r) => <span className={r.overdue ? "text-red-600 font-medium" : ""}>{fmtDate(r.dueDate)}{r.overdue ? " · overdue" : ""}</span> },
          { key: "source", header: "Source", render: (r) => <span className="text-xs text-muted-foreground">{r.source}</span> },
        ]} />

      {/* Create modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-dsar-modal">
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">New privacy request</h3>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="dsar-type-select">
                {DSAR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input placeholder="Requester email" value={form.requesterEmail}
                onChange={(e) => setForm({ ...form, requesterEmail: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="dsar-email-input" />
              <input placeholder="Requester name (optional)" value={form.requesterName}
                onChange={(e) => setForm({ ...form, requesterName: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <input placeholder="Customer ID (optional — links the subject)" value={form.customerId}
                onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="dsar-customer-input" />
              <textarea placeholder="Message (optional)" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" rows={2} />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={create} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="submit-dsar-btn">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="dsar-detail-drawer">
          <button className="flex-1 bg-black/40" onClick={() => setActive(null)} aria-label="close" />
          <div className="w-full max-w-lg bg-card border-l border-border h-full overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-sm text-teal-600">{active.requestNumber}</div>
                <h3 className="text-lg font-semibold text-foreground capitalize">{active.type} request</h3>
              </div>
              <button onClick={() => setActive(null)} data-testid="close-dsar-drawer"><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>

            <div className="space-y-1 text-sm mb-4">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge label={active.status} kind="status" /></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Requester</span><span>{active.requesterEmail}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Due</span><span>{fmtDate(active.dueDate)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Source</span><span>{active.source}</span></div>
              {active.customerId && <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-mono text-xs">{active.customerId.slice(0, 12)}</span></div>}
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {canFulfill && (
                <button onClick={fulfill} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5" data-testid="fulfill-btn">
                  <PlayCircle className="h-4 w-4" /> {active.type === "erasure" ? "Anonymize subject" : "Compile data package"}
                </button>
              )}
              {["access", "export", "portability"].includes(active.type) && active.status === "Completed" && (
                <button onClick={downloadPackage} className="inline-flex items-center gap-2 rounded-lg border border-border text-sm px-3 py-1.5" data-testid="download-package-btn">
                  <Download className="h-4 w-4" /> Download package
                </button>
              )}
            </div>

            <div className="mb-5">
              <div className="text-xs font-semibold text-muted-foreground mb-2">UPDATE STATUS</div>
              <div className="flex flex-wrap gap-2">
                {DSAR_STATUSES.map((s) => (
                  <button key={s} onClick={() => setStatus(s)} disabled={s === active.status}
                    className="rounded-lg border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-40"
                    data-testid={`status-${s.replace(/\s/g, "-")}`}>{s}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-2">NOTES</div>
              <div className="space-y-2 mb-2">
                {(active.notes || []).map((n) => (
                  <div key={n.id} className="rounded-lg bg-muted/40 p-2 text-sm">
                    <div className="text-foreground">{n.body}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{n.authorEmail} · {fmtDate(n.createdOn)}</div>
                  </div>
                ))}
                {(!active.notes || active.notes.length === 0) && <div className="text-xs text-muted-foreground">No notes yet.</div>}
              </div>
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="dsar-note-input" />
                <button onClick={addNote} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="add-note-btn">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
